import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { useUsmPrefs } from "@/contexts/UsmPrefsContext";
import { PageHeader } from "./UsmLayout";
import HubTabs from "./hub/HubTabs";
import ScannerButton from "@/components/usm/ScannerButton";
import { toast } from "sonner";
import {
  ScanLine, X, Trash2, Plus, Minus, ShoppingCart, CheckCircle2, Search,
  User, Receipt, Eraser, Loader2, RotateCcw,
} from "lucide-react";
import Can from "@/components/usm/Can";

/**
 * Ürün Satış (Sales Terminal) — tablet/touch-optimised rewrite.
 *
 *  Layout:
 *    [ LEFT, 2/3 wide ]                          [ RIGHT, 1/3 wide ]
 *    Big scan box (auto-add)                     Müşteri (collapsible)
 *    Cart table (roomy, touch-friendly)          Toplamlar (huge grand total)
 *                                                Ödemeler
 *                                                Quick action grid (44px+)
 *
 *  Key UX rules:
 *    • Scan barcode  → instantly added to cart (no extra button click).
 *    • Cart persists across route changes / refresh via localStorage
 *      (`usm.pos.cart.v1`). Cleared only on explicit clear or successful sale.
 *    • Focus auto-returns to scan input after every interaction.
 *    • All controls ≥44px touch target (iPad / Android tablet).
 *    • Jewelry items are unique inventory pieces — duplicate add stays blocked
 *      server-side AND client-side (matches inventory model). Communicated
 *      clearly to user via toast.
 *
 *  Backend contracts unchanged:
 *    GET /api/usm/price-lookup/{code}        (resolves EAN-8/stock_code)
 *    GET /api/usm/inventory?q=…&status=active (search)
 *    POST /api/usm/sales                     (atomic sale + items + payments)
 */

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

const PAYMENT_TYPES = [
  { v: "cash",     l: "Nakit TRY",    cur: "TRY" },
  { v: "cash_usd", l: "Nakit USD",    cur: "USD" },
  { v: "cash_eur", l: "Nakit EUR",    cur: "EUR" },
  { v: "cash_sar", l: "Nakit SAR",    cur: "SAR" },
  { v: "cash_aed", l: "Nakit AED",    cur: "AED" },
  { v: "cash_kwd", l: "Nakit KWD",    cur: "KWD" },
  { v: "card",     l: "Kredi Kartı",  cur: "TRY" },
  { v: "transfer", l: "Havale/EFT",   cur: "TRY" },
  { v: "other",    l: "Diğer",        cur: "TRY" },
];
// Map UI payment types → backend-accepted values (backend still validates
// against {cash, card, transfer, other}; the currency is passed separately).
const PAYMENT_TYPE_MAP = {
  cash:     { type: "cash",     currency: "TRY" },
  cash_usd: { type: "cash",     currency: "USD" },
  cash_eur: { type: "cash",     currency: "EUR" },
  cash_sar: { type: "cash",     currency: "SAR" },
  cash_aed: { type: "cash",     currency: "AED" },
  cash_kwd: { type: "cash",     currency: "KWD" },
  card:     { type: "card",     currency: "TRY" },
  transfer: { type: "transfer", currency: "TRY" },
  other:    { type: "other",    currency: "TRY" },
};

const STORAGE_KEY = "usm.pos.cart.v1";
const LAST_SALE_KEY = "usm.pos.lastSaleId.v1";

// ─── Persisted cart helpers ────────────────────────────────────────────────
function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch { return null; }
}

function savePersisted(snapshot) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot)); } catch { /* quota etc */ }
}

function clearPersisted() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
}

// ─── Audio feedback (WebAudio, no asset files) ─────────────────────────────
function beep(type) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = window.__usmAudioCtx || (window.__usmAudioCtx = new AC());
    if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* no-op */ } }
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    if (type === "ok") {
      o.frequency.setValueAtTime(880, now);
      o.frequency.exponentialRampToValueAtTime(1320, now + 0.06);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.15, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      o.start(now); o.stop(now + 0.14);
    } else {
      o.type = "square";
      o.frequency.setValueAtTime(220, now);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.18, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      o.start(now); o.stop(now + 0.24);
    }
  } catch { /* audio unavailable, silent */ }
}

export default function Pos() {
  const { employee } = useUsmAuth();
  const { prefs } = useUsmPrefs();
  // Phase 54.2 — pass the active branch on every price-lookup so the
  // scanner returns the row of THIS branch, not another branch's row.
  const activeBranchId = prefs?.active_branch_id || employee?.branch_id || null;
  const navigate = useNavigate();
  const scanRef = useRef(null);

  // Initialise from persisted snapshot if present.
  const persisted = useRef(loadPersisted()).current;

  const [scanCode, setScanCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState("");

  const [cart, setCart] = useState(() => persisted?.cart || []);
  const [customer, setCustomer] = useState(() => persisted?.customer || { name: "", phone: "" });
  const [discount, setDiscount] = useState(() => (persisted?.discount ?? ""));
  const [tax, setTax] = useState(() => (persisted?.tax ?? ""));
  const [payments, setPayments] = useState(() => persisted?.payments || [{ payment_type: "cash", amount: "", reference_no: "", foreign_amount: "", bank_account_id: "" }]);
  const [showCustomer, setShowCustomer] = useState(() => !!(persisted?.customer?.name || persisted?.customer?.phone));
  const [submitting, setSubmitting] = useState(false);
  // Phase 53.6 — Dynamic Bank list. Fetch active banks so card/transfer
  // payments route to a specific bank account. If no banks exist, we keep
  // the classic behaviour (auto-route to seed BANK account).
  const [banks, setBanks] = useState([]);
  useEffect(() => {
    usmApi.get("/accounting/banks?status=active")
      .then((r) => setBanks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBanks([]));
  }, []);

  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Auto-focus scan field on mount and persist cart on any change.
  useEffect(() => { scanRef.current?.focus(); }, []);
  useEffect(() => {
    if (cart.length === 0 && !customer.name && !customer.phone && Number(discount) === 0 && Number(tax) === 0) {
      clearPersisted();
    } else {
      savePersisted({ cart, customer, discount, tax, payments });
    }
  }, [cart, customer, discount, tax, payments]);

  const focusScan = useCallback(() => {
    setTimeout(() => scanRef.current?.focus(), 0);
  }, []);

  // Subtotal / grand_total derived
  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, l) => s + Math.max(0, (Number(l.unit_price) || 0) * (Number(l.qty) || 0) - (Number(l.line_discount) || 0)), 0);
    const grand = Math.max(0, subtotal - (Number(discount) || 0) + (Number(tax) || 0));
    const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const due = Math.max(0, grand - paid);
    const over = Math.max(0, paid - grand);
    // Derive multi-currency Grand Total from any line's price snapshot
    // (all lines added in the same session share the same TCMB rates).
    let grandByCurrency = null;
    const ref = cart.find((l) => l.prices && Number(l.prices.TRY) > 0);
    if (ref) {
      const trA = Number(ref.prices.TRY);
      grandByCurrency = {};
      for (const c of ["USD", "EUR", "SAR", "AED", "KWD"]) {
        if (ref.prices[c] != null && Number.isFinite(ref.prices[c])) {
          grandByCurrency[c] = grand * (Number(ref.prices[c]) / trA);
        }
      }
    }
    return { subtotal, grand, paid, due, over, grandByCurrency };
  }, [cart, discount, tax, payments]);

  const addItem = useCallback((item, { silent } = {}) => {
    if (!item || !item.id) {
      if (!silent) { beep("err"); toast.error("Ürün bulunamadı"); }
      return false;
    }
    // Phase 53.7 — Controlled oversell. Sales are allowed even when
    // quantity is 0 or already negative. Only workflow-locked statuses
    // (reserved/repair/transfer/lost) still block; server also enforces.
    const lockedStatuses = ["reserved", "repair", "transfer", "lost"];
    if (item.status && lockedStatuses.includes(item.status)) {
      beep("err");
      toast.error(`${item.stock_code} işlemde (durum: ${item.status})`);
      return false;
    }
    // If the same inventory_id is already in the cart, always allow bumping
    // qty (oversell permitted). The Phase 36 "unique piece" UX still shows a
    // gentle nudge for single-piece items, but no longer blocks.
    const existing = cart.find((l) => l.inventory_id === item.id);
    if (existing) {
      const next = Number(existing.qty || 1) + 1;
      setCart((prev) => prev.map((l) => l.tempId === existing.tempId ? { ...l, qty: next } : l));
      beep("ok");
      if (!silent) toast.success(`${item.stock_code || item.name} adedi ${next} oldu`);
      return true;
    }
    setCart((prev) => [
      ...prev,
      {
        tempId: crypto.randomUUID(),
        inventory_id: item.id,
        stock_code: item.stock_code || item.barcode || "",
        ean8_barcode: item.ean8_barcode || "",
        name: item.name || "",
        material: item.material || "",
        purity: item.purity || "",
        weight_grams: item.weight_grams,
        branch_id: item.branch_id,
        photo: (item.photo_urls || [])[0],
        qty: 1,
        on_hand: Number(item.quantity ?? 1),  // snapshot for client-side qty caps
        unit_price: Number(item.sale_price_try) || 0,
        // Multi-currency snapshot at the moment the item enters the cart.
        // We keep it on the line for instant rendering — no extra fetches.
        prices: item.prices || null,
        line_discount: 0,
      },
    ]);
    beep("ok");
    if (!silent) toast.success(`${item.stock_code || item.name} sepete eklendi`);
    return true;
  }, [cart]);

  // Auto-add on Enter (scanners send Enter as terminator).
  // Phase 54.26 — factor the scan flow so it can be triggered by the
  // Phone Scanner (which delivers the code directly, not through the
  // input's state). The input-driven Enter path still routes through
  // this same function → single barcode pipeline.
  const processCode = useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    setScanning(true); setScanError("");
    try {
      const r = await usmApi.get(`/price-lookup/${encodeURIComponent(code)}`, { params: activeBranchId ? { branch_id: activeBranchId } : {} });
      addItem(r.data);
      setScanCode("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Ürün bulunamadı";
      setScanError(msg);
      beep("err");
      toast.error(msg);
    } finally {
      setScanning(false);
      focusScan();
    }
  }, [addItem, focusScan, activeBranchId]);

  const handleScanSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    await processCode(scanCode);
  }, [scanCode, processCode]);

  // Debounced product search by name (live results, no Enter required)
  useEffect(() => {
    if (!searchTerm.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        // Drop the status=active filter so OUT_OF_STOCK products remain
        // visible in POS search (Phase 36). Cashier sees them with a red
        // "STOKTA YOK" badge and can't add them.
        const scopeParam = activeBranchId ? `&branch_id=${activeBranchId}` : "";
        const r = await usmApi.get(`/inventory?q=${encodeURIComponent(searchTerm.trim())}&limit=10${scopeParam}`);
        setSearchResults(r.data.items || []);
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const updateLine = (tempId, patch) => setCart((prev) => prev.map((l) => l.tempId === tempId ? { ...l, ...patch } : l));
  const removeLine = (tempId) => { setCart((prev) => prev.filter((l) => l.tempId !== tempId)); focusScan(); };

  const setPayment = (idx, patch) => setPayments((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const addPayment = () => setPayments((prev) => [...prev, { payment_type: "cash", amount: "", reference_no: "", foreign_amount: "", bank_account_id: "" }]);
  const removePayment = (idx) => setPayments((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const fillRemainingOnFirst = () => {
    if (totals.due > 0) {
      setPayments((prev) => prev.map((p, i) => i === 0 ? { ...p, amount: (Number(p.amount) || 0) + totals.due } : p));
    }
  };

  const clearAll = (opts = {}) => {
    if (!opts.skipConfirm && cart.length > 0) {
      if (!window.confirm("Sepet boşaltılsın mı? Tüm satış bilgileri silinecek.")) return;
    }
    setCart([]); setDiscount(""); setTax(""); setCustomer({ name: "", phone: "" });
    setPayments([{ payment_type: "cash", amount: "", reference_no: "", foreign_amount: "", bank_account_id: "" }]);
    setScanCode(""); setSearchTerm(""); setSearchResults([]);
    setShowCustomer(false);
    clearPersisted();
    focusScan();
  };

  const submit = async () => {
    if (!cart.length) { beep("err"); toast.error("Sepet boş"); return; }
    if (Math.abs(totals.paid - totals.grand) > 0.01) {
      beep("err");
      toast.error(`Ödeme toplam ile eşleşmiyor (${TRY.format(totals.paid)} vs ${TRY.format(totals.grand)})`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        branch_id: activeBranchId || cart[0]?.branch_id, // Phase 54.2 — prefer active branch context
        customer_name: customer.name || null,
        customer_phone: customer.phone || null,
        discount_amount: Number(discount) || 0,
        tax_amount: Number(tax) || 0,
        items: cart.map((l) => ({
          inventory_id: l.inventory_id,
          qty: Number(l.qty) || 1,
          unit_price: Number(l.unit_price) || 0,
          line_discount: Number(l.line_discount) || 0,
        })),
        payments: payments.filter((p) => (Number(p.amount) || 0) > 0).map((p) => {
          const map = PAYMENT_TYPE_MAP[p.payment_type] || { type: p.payment_type, currency: "TRY" };
          return {
            payment_type: map.type,
            amount: Number(p.amount),
            reference_no: p.reference_no || null,
            currency: map.currency,
            foreign_amount: map.currency !== "TRY" && p.foreign_amount !== ""
              ? Number(p.foreign_amount) : null,
            bank_account_id: (map.type === "card" || map.type === "transfer") && p.bank_account_id
              ? p.bank_account_id : null,
          };
        }),
      };
      const r = await usmApi.post("/sales", payload);
      try { localStorage.setItem(LAST_SALE_KEY, r.data.id); } catch { /* no-op */ }
      clearAll({ skipConfirm: true });
      beep("ok");
      // Phase 52.2 — auto-print popup removed. Sale completes silently;
      // operator stays on POS ready for the next customer. Receipt is still
      // reachable via viewLastReceipt() or Sales History.
      toast.success("Satış başarıyla tamamlandı.", {
        description: `Fiş no: ${r.data.sale_no}`,
      });
      focusScan();
    } catch (e) {
      beep("err");
      toast.error(e?.response?.data?.detail || "Satış başarısız");
    } finally {
      setSubmitting(false);
    }
  };

  const viewLastReceipt = () => {
    let id;
    try { id = localStorage.getItem(LAST_SALE_KEY); } catch { /* no-op */ }
    if (!id) { toast.info("Son satış bulunamadı"); return; }
    navigate(`/usm/sales/${id}`);
  };

  const canSubmit = !submitting && cart.length > 0 && Math.abs(totals.paid - totals.grand) <= 0.01;

  return (
    <div data-testid="usm-pos" className="-mx-4 sm:mx-0 md:flex md:flex-col md:h-[calc(100dvh-3rem)] md:overflow-hidden md:-mt-3 md:-mb-5">
      <HubTabs section="pos" />
      {/* Compact POS header — full header on mobile/desktop, hidden on tablet for vertical space */}
      <div className="md:hidden">
        <PageHeader
          title="Ürün Satış"
          breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Ürün Satış" }]}
          actions={
            employee && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] text-[#71717A]">
                <User size={12} /> {employee.full_name || employee.username}
              </span>
            )
          }
        />
      </div>
      <div className="hidden md:flex items-center justify-between mb-2">
        <h1 className="font-heading text-xl leading-none">Ürün Satış</h1>
        {employee && (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
            <User size={11} /> {employee.full_name || employee.username}
          </span>
        )}
      </div>

      <div className="md:flex-1 md:min-h-0 grid grid-cols-1 md:grid-cols-[1fr_360px] lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px] gap-4 md:gap-3">
        {/* ════════ LEFT pane: scan + search + cart ════════ */}
        <section className="flex flex-col gap-4 md:gap-3 min-w-0 md:overflow-y-auto md:min-h-0 md:pr-1">
          {/* Scan / search bar */}
          <div className="bg-white border border-[#E7E5E4] p-4 sm:p-5">
            <form onSubmit={handleScanSubmit}>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">
                Barkod / Stok Kodu — Okutun
              </label>
              {/* Phase 54.26.2 — ScannerButton is a proper flex sibling of
                  the input container (matches PriceLookup / InventoryEditor
                  layout) so its tap target is never intercepted by the
                  underlying input on tablets. */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <ScanLine size={26} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C8A96A] pointer-events-none" />
                  <input
                    ref={scanRef}
                    data-testid="pos-scan-input"
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    autoFocus autoComplete="off" spellCheck={false}
                    placeholder="EAN-8 veya stok kodu — Enter ile ekle"
                    className="w-full pl-14 pr-10 py-5 border border-[#E7E5E4] focus:border-[#C8A96A] focus:ring-2 focus:ring-[#C8A96A]/20 outline-none text-2xl font-mono tracking-wider rounded-sm"
                  />
                  {scanning && <Loader2 size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C8A96A] animate-spin" />}
                </div>
                <ScannerButton onScan={processCode} />
              </div>
              {/* Hidden submit button to support Enter on touch keyboards */}
              <button data-testid="pos-scan-submit" type="submit" className="sr-only">Ekle</button>
              {scanError && <div data-testid="pos-scan-error" className="mt-2.5 text-sm text-red-600 font-medium">{scanError}</div>}
              <p className="mt-2 text-[11px] text-[#A1A1AA]">
                Okutma sonrası ürün otomatik sepete eklenir.
              </p>
            </form>

            <div className="mt-4 pt-4 border-t border-[#F5F5F4]">
              <label className="block text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Ürün Ara</label>
              <div className="relative">
                <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input
                  data-testid="pos-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ürün adı, stok kodu veya barkod..."
                  className="w-full pl-11 pr-3 py-3 border border-[#E7E5E4] text-base"
                />
              </div>
              {searching && <p className="text-[11px] text-[#71717A] mt-2">Aranıyor...</p>}
              {searchResults.length > 0 && (
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {searchResults.map((it) => {
                    const oos = it.stock_state === "out_of_stock" || (it.quantity != null && Number(it.quantity) <= 0);
                    const low = !oos && it.stock_state === "low_stock";
                    const neg = it.quantity != null && Number(it.quantity) < 0;
                    return (
                    <li key={it.id}>
                      <button
                        data-testid={`pos-search-result-${it.stock_code || it.id}`}
                        onClick={() => {
                          // Phase 53.7 — Controlled oversell. No longer blocks
                          // on OOS; sale proceeds and quantity goes negative.
                          addItem(it); setSearchTerm(""); setSearchResults([]); focusScan();
                        }}
                        className={
                          "w-full text-left p-3 border transition-colors min-h-[60px] " +
                          (oos
                            ? "bg-amber-50/60 border-amber-100 hover:bg-amber-50"
                            : "bg-[#FAFAF9] hover:bg-[#FBF5E8] border-[#F5F5F4]")
                        }
                      >
                        <div className="flex items-center gap-3">
                          {it.photo_urls?.[0]
                            ? <img src={it.photo_urls[0]} alt="" className="w-12 h-12 object-cover" />
                            : <div className="w-12 h-12 bg-white border border-[#F5F5F4]" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-[11px] text-[#71717A] flex items-center gap-2">
                              <span>{it.stock_code}</span>
                              {neg && (
                                <span data-testid={`pos-search-neg-${it.stock_code}`} className="px-1.5 py-0.5 bg-red-600 text-white text-[9.5px] uppercase tracking-wider rounded-sm">Eksi ({it.quantity})</span>
                              )}
                              {oos && !neg && (
                                <span data-testid={`pos-search-oos-${it.stock_code}`} className="px-1.5 py-0.5 bg-amber-600 text-white text-[9.5px] uppercase tracking-wider rounded-sm">Stokta Yok</span>
                              )}
                              {low && (
                                <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[9.5px] uppercase tracking-wider rounded-sm">Stok Az ({it.quantity})</span>
                              )}
                            </div>
                            <div className="text-sm font-medium truncate">{it.name}</div>
                            <div className="font-finance text-base text-[#C8A96A]">{it.sale_price_try == null ? "—" : TRY.format(it.sale_price_try)}</div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );})}
                </ul>
              )}
            </div>
          </div>

          {/* Cart */}
          <div className="bg-white border border-[#E7E5E4] flex flex-col">
            <div className="px-5 py-3.5 border-b border-[#E7E5E4] flex items-center justify-between">
              <h3 className="text-[12px] uppercase tracking-[0.2em] text-[#27272A] inline-flex items-center gap-2 font-medium">
                <ShoppingCart size={16} /> Sepet
                <span className="font-mono text-[#C8A96A]">({cart.length})</span>
              </h3>
              {cart.length > 0 && (
                <button
                  data-testid="pos-cart-clear-inline"
                  onClick={() => clearAll()}
                  className="text-[11px] uppercase tracking-[0.15em] text-red-600 hover:underline inline-flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Temizle
                </button>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="min-h-[280px] flex flex-col items-center justify-center text-center p-8 text-[#A8A29E]">
                <ShoppingCart size={36} className="text-[#E7E5E4] mb-3" />
                <p className="text-base">Sepet boş.</p>
                <p className="text-sm mt-1">Barkod okutarak veya arayarak ürün ekleyin.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
                    <tr>
                      <th className="text-left px-4 py-3">Ürün</th>
                      <th className="text-right px-3 py-3 w-28">Adet</th>
                      <th className="text-right px-3 py-3 w-32">Birim Fiyat</th>
                      <th className="text-right px-3 py-3 w-28">İndirim</th>
                      <th className="text-right px-4 py-3 w-32">Tutar</th>
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((l) => {
                      const lineTotal = Math.max(0, (Number(l.unit_price) || 0) * (Number(l.qty) || 0) - (Number(l.line_discount) || 0));
                      return (
                        <tr key={l.tempId} data-testid={`pos-cart-row-${l.stock_code}`} className="border-t border-[#F5F5F4] hover:bg-[#FAFAF9]/40">
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              {l.photo
                                ? <img src={l.photo} alt="" className="w-12 h-12 object-cover" />
                                : <div className="w-12 h-12 bg-[#FAFAF9] border border-[#F5F5F4]" />}
                              <div className="min-w-0">
                                <div className="font-mono text-[11px] text-[#71717A]">
                                  {l.stock_code}{l.ean8_barcode && <span className="ml-1.5 text-[10px]">· {l.ean8_barcode}</span>}
                                </div>
                                <div className="font-medium text-[15px] truncate max-w-[280px]">{l.name}</div>
                                <div className="text-[12px] text-[#71717A]">
                                  {l.material || l.purity}{l.weight_grams != null && ` · ${l.weight_grams}g`}
                                </div>
                                <CurrencyPills prices={l.prices} />
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3.5 text-right">
                            <div className="inline-flex items-center border border-[#E7E5E4]">
                              <button
                                onClick={() => updateLine(l.tempId, { qty: Math.max(1, (Number(l.qty) || 1) - 1) })}
                                className="px-3 py-2.5 hover:bg-[#FAFAF9] active:bg-[#F5F5F4]"
                                aria-label="Adet azalt"
                              ><Minus size={14} /></button>
                              <input
                                data-testid={`pos-qty-${l.stock_code}`}
                                type="number" min="1" value={l.qty}
                                onChange={(e) => {
                                  // Phase 53.7 — Oversell permitted; no cap.
                                  const v = Math.max(1, parseInt(e.target.value || "1", 10));
                                  updateLine(l.tempId, { qty: v });
                                }}
                                className="w-14 text-center text-base border-0 outline-none"
                              />
                              <button
                                onClick={() => {
                                  // Phase 53.7 — Oversell permitted; no cap.
                                  const next = (Number(l.qty) || 1) + 1;
                                  updateLine(l.tempId, { qty: next });
                                }}
                                className="px-3 py-2.5 hover:bg-[#FAFAF9] active:bg-[#F5F5F4]"
                                aria-label="Adet artır"
                              ><Plus size={14} /></button>
                            </div>
                            {l.on_hand > 1 && (
                              <div className="text-[10px] text-[#A1A1AA] mt-0.5 font-mono">Stok: {l.on_hand}</div>
                            )}
                          </td>
                          <td className="px-3 py-3.5 text-right">
                            <input
                              data-testid={`pos-price-${l.stock_code}`}
                              type="number" min="0" step="0.01" value={l.unit_price}
                              onChange={(e) => updateLine(l.tempId, { unit_price: e.target.value === "" ? 0 : Number(e.target.value) })}
                              className="w-28 px-2 py-2 border border-[#E7E5E4] text-right font-finance text-base"
                            />
                          </td>
                          <td className="px-3 py-3.5 text-right">
                            <input
                              data-testid={`pos-disc-${l.stock_code}`}
                              type="number" min="0" step="0.01" value={l.line_discount}
                              onChange={(e) => updateLine(l.tempId, { line_discount: e.target.value === "" ? 0 : Number(e.target.value) })}
                              className="w-24 px-2 py-2 border border-[#E7E5E4] text-right font-finance text-base"
                            />
                          </td>
                          <td className="px-4 py-3.5 text-right font-finance text-base font-medium">{TRY.format(lineTotal)}</td>
                          <td className="px-3 py-3.5 text-right">
                            <button
                              data-testid={`pos-remove-${l.stock_code}`}
                              onClick={() => removeLine(l.tempId)}
                              className="p-3 text-red-600 hover:bg-red-50 rounded-sm"
                              aria-label="Sepetten çıkar"
                            ><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ════════ RIGHT pane: customer + totals + payments + quick actions ════════ */}
        <aside className="flex flex-col gap-4 md:gap-3 min-w-0 md:min-h-0">
          {/* Scrollable top section — Customer + Totals + Payments */}
          <div className="flex flex-col gap-4 md:gap-3 min-w-0 md:overflow-y-auto md:flex-1 md:min-h-0 md:pr-1">
          {/* Customer (collapsible to save space) */}
          <div className="bg-white border border-[#E7E5E4]">
            <button
              onClick={() => setShowCustomer((v) => !v)}
              data-testid="pos-customer-toggle"
              className="w-full px-4 py-3.5 text-left flex items-center justify-between hover:bg-[#FAFAF9]"
            >
              <span className="text-[12px] uppercase tracking-[0.2em] text-[#27272A] font-medium inline-flex items-center gap-2">
                <User size={14} /> Müşteri
                {!showCustomer && (customer.name || customer.phone) && <span className="text-[#C8A96A] normal-case tracking-normal font-normal text-sm">· {customer.name || customer.phone}</span>}
              </span>
              <span className="text-[#71717A] text-base">{showCustomer ? "−" : "+"}</span>
            </button>
            {showCustomer && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-[#F5F5F4] pt-3">
                <input
                  data-testid="pos-customer-name" placeholder="Ad Soyad" value={customer.name}
                  onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                  className="px-3 py-3 border border-[#E7E5E4] text-base"
                />
                <input
                  data-testid="pos-customer-phone" placeholder="Telefon" value={customer.phone}
                  onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                  className="px-3 py-3 border border-[#E7E5E4] text-base"
                />
              </div>
            )}
          </div>

          {/* Totals + Grand total */}
          <div className="bg-white border border-[#E7E5E4] p-5 md:p-3.5">
            <h3 className="text-[12px] uppercase tracking-[0.2em] text-[#27272A] font-medium mb-3 md:mb-2">Toplamlar</h3>
            <div className="space-y-2.5 md:space-y-1.5 text-[15px]">
              <Row label="Ara Toplam" value={TRY.format(totals.subtotal)} />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#71717A]">İndirim</span>
                <input
                  data-testid="pos-sale-discount" type="number" min="0" step="0.01" value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0,00"
                  className="w-32 px-2 py-2 md:py-1.5 border border-[#E7E5E4] text-right font-finance text-base"
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#71717A]">Vergi</span>
                <input
                  data-testid="pos-sale-tax" type="number" min="0" step="0.01" value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  placeholder="0,00"
                  className="w-32 px-2 py-2 md:py-1.5 border border-[#E7E5E4] text-right font-finance text-base"
                />
              </div>
            </div>

            {/* Hero Grand Total — always highly visible. Multi-currency
                breakdown stacks beneath so cashier can quote in any FX. */}
            <div className="mt-4 md:mt-3 -mx-5 md:-mx-3.5 -mb-5 md:-mb-3.5 px-5 md:px-3.5 py-4 md:py-3 bg-[#0A0A0A] text-white">
              <div className="text-[10.5px] uppercase tracking-[0.25em] text-[#C8A96A] mb-0.5">Genel Toplam</div>
              <div
                data-testid="pos-grand-total"
                className="font-finance text-[clamp(1.75rem,4.5vw,2.5rem)] leading-none tracking-tight"
              >
                {TRY.format(totals.grand)}
              </div>
              {totals.grandByCurrency && totals.grand > 0 && (
                <div
                  data-testid="pos-grand-total-currencies"
                  className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-x-4 gap-y-2 text-[16px] font-mono"
                >
                  {Object.entries(totals.grandByCurrency).map(([c, v]) => (
                    <div
                      key={c}
                      data-testid={`pos-grand-total-${c}`}
                      className="flex items-baseline justify-between text-white/85"
                    >
                      <span className="text-white/55 text-[12px]">≈ {c}</span>
                      <span>{formatFx(c, v)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payments */}
          <div className="bg-white border border-[#E7E5E4] p-5 md:p-3.5">
            <div className="flex items-center justify-between mb-3 md:mb-2">
              <h3 className="text-[12px] uppercase tracking-[0.2em] text-[#27272A] font-medium">Ödemeler</h3>
              <button
                data-testid="pos-add-payment"
                onClick={addPayment}
                className="text-[11px] uppercase tracking-[0.15em] text-[#C8A96A] hover:underline"
              >+ Karışık Ödeme</button>
            </div>
            <div className="space-y-2 md:space-y-1.5">
              {payments.map((p, i) => {
                const typeMeta = PAYMENT_TYPES.find((t) => t.v === p.payment_type) || PAYMENT_TYPES[0];
                const isForeign = typeMeta.cur !== "TRY";
                const isBankRoutable = p.payment_type === "card" || p.payment_type === "transfer";
                return (
                <div key={i} className="border border-[#E7E5E4] p-2 space-y-1.5" data-testid={`pos-payment-row-${i}`}>
                  <div className="grid grid-cols-[1fr_140px_44px] gap-2 items-center">
                    <select
                      data-testid={`pos-pay-type-${i}`}
                      value={p.payment_type}
                      onChange={(e) => setPayment(i, { payment_type: e.target.value })}
                      className="px-2 py-3 md:py-2 border border-[#E7E5E4] text-base"
                    >
                      {PAYMENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <input
                      data-testid={`pos-pay-amount-${i}`}
                      type="number" min="0" step="0.01" placeholder={isForeign ? "TRY karşılığı" : "0,00"} value={p.amount}
                      onChange={(e) => setPayment(i, { amount: e.target.value === "" ? "" : Number(e.target.value) })}
                      className="px-2 py-3 md:py-2 border border-[#E7E5E4] text-right font-finance text-base"
                    />
                    {payments.length > 1 ? (
                      <button
                        onClick={() => removePayment(i)}
                        className="p-3 md:p-2 text-red-600 hover:bg-red-50"
                        aria-label="Ödemeyi sil"
                      ><X size={14} /></button>
                    ) : <div />}
                  </div>
                  {isForeign && (
                    <div className="grid grid-cols-[1fr_1fr] gap-2">
                      <input
                        data-testid={`pos-pay-foreign-${i}`}
                        type="number" min="0" step="0.01"
                        placeholder={`${typeMeta.cur} tutarı`}
                        value={p.foreign_amount || ""}
                        onChange={(e) => setPayment(i, { foreign_amount: e.target.value })}
                        className="px-2 py-2 border border-[#E7E5E4] text-right font-finance text-sm"
                      />
                      <div className="px-2 py-2 border border-dashed border-[#E7E5E4] bg-[#FAFAF9] text-[11px] text-[#71717A] flex items-center">
                        <span className="uppercase tracking-[0.15em] mr-1">Kur elle:</span>
                        <span className="font-mono text-[#0A0A0A]">{p.foreign_amount && Number(p.foreign_amount) > 0 && p.amount && Number(p.amount) > 0
                          ? (Number(p.amount) / Number(p.foreign_amount)).toFixed(2)
                          : "—"}</span>
                      </div>
                    </div>
                  )}
                  {isBankRoutable && banks.length > 0 && (
                    <select
                      data-testid={`pos-pay-bank-${i}`}
                      value={p.bank_account_id || ""}
                      onChange={(e) => setPayment(i, { bank_account_id: e.target.value })}
                      className="w-full px-2 py-2 border border-[#E7E5E4] text-sm bg-white"
                    >
                      <option value="">Banka seçin (opsiyonel)</option>
                      {banks.map((b) => <option key={b.id} value={b.account_id}>{b.name}{b.account_currency ? ` · ${b.account_currency}` : ""}</option>)}
                    </select>
                  )}
                </div>
              );})}
            </div>

            <button
              data-testid="pos-fill-remaining"
              onClick={fillRemainingOnFirst}
              disabled={totals.due <= 0}
              className="mt-3 md:mt-2 w-full text-[11px] uppercase tracking-[0.15em] text-[#71717A] hover:bg-[#FAFAF9] py-2.5 md:py-2 border border-[#E7E5E4] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Kalanı Ata · {TRY.format(totals.due)}
            </button>

            <div className="mt-3 md:mt-2 grid grid-cols-2 gap-2 text-[12px] uppercase tracking-[0.15em]">
              <div className="bg-[#FAFAF9] px-3 py-2.5 md:py-2">
                <div className="text-[#71717A] text-[10px]">Ödenen</div>
                <div data-testid="pos-paid" className="font-finance text-[15px] text-[#27272A] mt-0.5 normal-case tracking-normal">{TRY.format(totals.paid)}</div>
              </div>
              {totals.over > 0 ? (
                <div className="bg-emerald-50 px-3 py-2.5 md:py-2">
                  <div className="text-emerald-700 text-[10px]">Para Üstü</div>
                  <div data-testid="pos-over" className="font-finance text-[15px] text-emerald-700 mt-0.5 normal-case tracking-normal">{TRY.format(totals.over)}</div>
                </div>
              ) : (
                <div className={"px-3 py-2.5 md:py-2 " + (totals.due > 0 ? "bg-red-50" : "bg-[#FAFAF9]")}>
                  <div className={"text-[10px] " + (totals.due > 0 ? "text-red-700" : "text-[#71717A]")}>Kalan</div>
                  <div data-testid="pos-due" className={"font-finance text-[15px] mt-0.5 normal-case tracking-normal " + (totals.due > 0 ? "text-red-700" : "text-[#27272A]")}>{TRY.format(totals.due)}</div>
                </div>
              )}
            </div>
          </div>

          </div>

          {/* Quick Action Panel — always pinned to the bottom on md+, full-width row on mobile */}
          <div className="grid grid-cols-2 gap-2.5 md:gap-2 md:shrink-0">
            <button
              data-testid="pos-action-submit"
              onClick={submit}
              disabled={!canSubmit}
              className="col-span-2 bg-[#0A0A0A] disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm uppercase tracking-[0.2em] font-medium py-5 md:py-3.5 min-h-[64px] md:min-h-[56px] inline-flex items-center justify-center gap-2.5 hover:bg-[#27272A] active:scale-[0.99] transition-transform"
            >
              {submitting ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle2 size={20} />}
              <span>{submitting ? "İşleniyor..." : "Tahsilat & Tamamla"}</span>
            </button>

            <Can perm="sales.manage">
              <Link
                to="/usm/sales/return"
                data-testid="pos-action-return"
                className="bg-white hover:bg-amber-50 border border-amber-200 text-amber-800 text-[12px] uppercase tracking-[0.15em] py-4 md:py-2.5 min-h-[60px] md:min-h-[50px] inline-flex flex-col items-center justify-center gap-1.5 md:gap-1 active:scale-[0.98] transition-transform"
              >
                <RotateCcw size={18} className="text-amber-700" />
                <span>İade</span>
              </Link>
            </Can>

            <button
              data-testid="pos-action-last-receipt"
              onClick={viewLastReceipt}
              className="bg-white hover:bg-[#FAFAF9] border border-[#E7E5E4] text-[#27272A] text-[12px] uppercase tracking-[0.15em] py-4 md:py-2.5 min-h-[60px] md:min-h-[50px] inline-flex flex-col items-center justify-center gap-1.5 md:gap-1 active:scale-[0.98] transition-transform"
            >
              <Receipt size={18} className="text-[#C8A96A]" />
              <span>Fiş Gör</span>
            </button>

            <button
              data-testid="pos-action-clear"
              onClick={() => clearAll()}
              disabled={cart.length === 0 && !customer.name && !customer.phone}
              className="col-span-2 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed border border-[#E7E5E4] text-red-700 text-[12px] uppercase tracking-[0.15em] py-4 md:py-2.5 min-h-[56px] md:min-h-[44px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Eraser size={16} />
              <span>Sepeti Temizle</span>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[#71717A]">{label}</span>
      <span className="font-finance">{value}</span>
    </div>
  );
}

// Multi-currency mini-pills shown under each cart line. Uses the snapshot
// that came down with the inventory/price-lookup response — no extra fetch.
const CURRENCY_ORDER = ["USD", "EUR", "SAR", "AED", "KWD"];
const SYMBOLS = { USD: "$", EUR: "€", SAR: "﷼", AED: "د.إ", KWD: "د.ك", TRY: "₺" };

function formatFx(code, value) {
  if (value == null || !Number.isFinite(value)) return "—";
  // Phase 50.1 — the currency code is already shown as the label next to the
  // value (e.g. "≈ USD"), so the value itself must not repeat the code or
  // symbol. Mirrors PriceLookup's PriceTile formatting for consistency.
  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
function CurrencyPills({ prices }) {
  if (!prices) return null;
  const entries = CURRENCY_ORDER
    .filter((c) => prices[c] != null && Number.isFinite(prices[c]))
    .map((c) => [c, prices[c]]);
  if (entries.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="cart-line-currencies">
      {entries.map(([c, v]) => (
        <span
          key={c}
          className="inline-flex items-baseline gap-0.5 px-1.5 py-0.5 bg-[#FAFAF9] ring-1 ring-[#F5F5F4] text-[10.5px] font-mono leading-none"
          data-testid={`cart-currency-${c}`}
        >
          <span className="text-[#A1A1AA] text-[9.5px]">{SYMBOLS[c] || c}</span>
          {/* Phase 54.25 #3 — Slightly larger value only. Layout, colors,
              spacing, and the black total panel are all untouched. */}
          <span className="text-[#0A0A0A] text-[12px] font-medium">{c === "USD" || c === "EUR"
            ? v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
            : v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          }</span>
        </span>
      ))}
    </div>
  );
}
